interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const INSTALLATION_STORAGE_KEY = 'tiquete360_pwa_installed'

export default defineNuxtPlugin(() => {
  const deferredPrompt = shallowRef<InstallPromptEvent | null>(null)
  const consumedPrompts = new WeakSet<Event>()
  const displayMode = window.matchMedia('(display-mode: standalone)')
  const isStandalone = () => displayMode.matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
  const installed = ref(isStandalone())
  try { installed.value ||= localStorage.getItem(INSTALLATION_STORAGE_KEY) === '1' } catch { /* Storage may be unavailable. */ }
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const installing = ref(false)
  const available = computed(() => !installed.value && (Boolean(deferredPrompt.value) || ios))

  function rememberInstallation(value: boolean) {
    installed.value = value
    try {
      if (value) localStorage.setItem(INSTALLATION_STORAGE_KEY, '1')
      else localStorage.removeItem(INSTALLATION_STORAGE_KEY)
    } catch { /* Installation remains usable without persistent storage. */ }
  }
  if (isStandalone()) rememberInstallation(true)

  function onInstallPrompt(event: Event) {
    if (isStandalone() || consumedPrompts.has(event)) return
    // A fresh native prompt confirms eligibility again, including after uninstall.
    event.preventDefault()
    rememberInstallation(false)
    deferredPrompt.value = event as InstallPromptEvent
  }
  function onInstalled() {
    rememberInstallation(true)
    deferredPrompt.value = null
  }
  function onDisplayModeChange() {
    if (isStandalone()) onInstalled()
  }
  function onStorage(event: StorageEvent) {
    if (event.key !== INSTALLATION_STORAGE_KEY) return
    try { if (event.storageArea !== localStorage) return } catch { return }
    installed.value = isStandalone() || event.newValue === '1'
    if (installed.value) deferredPrompt.value = null
  }

  window.addEventListener('beforeinstallprompt', onInstallPrompt)
  window.addEventListener('appinstalled', onInstalled)
  window.addEventListener('storage', onStorage)
  displayMode.addEventListener('change', onDisplayModeChange)
  if (import.meta.hot) import.meta.hot.dispose(() => {
    window.removeEventListener('beforeinstallprompt', onInstallPrompt)
    window.removeEventListener('appinstalled', onInstalled)
    window.removeEventListener('storage', onStorage)
    displayMode.removeEventListener('change', onDisplayModeChange)
  })

  return {
    provide: {
      pwaInstall: {
        ios,
        installed: computed(() => installed.value),
        visible: computed(() => !installed.value && (available.value || installing.value)),
        available,
        installing,
        async install(): Promise<boolean> {
          const prompt = deferredPrompt.value
          if (!prompt || installing.value || installed.value) return false
          // Native install prompts are single-use, even when dismissed.
          deferredPrompt.value = null
          consumedPrompts.add(prompt)
          installing.value = true
          try {
            await prompt.prompt()
            const choice = await prompt.userChoice
            if (choice.outcome === 'accepted') rememberInstallation(true)
            return true
          } finally {
            installing.value = false
          }
        }
      }
    }
  }
})
